import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';

import { updateLoginEmpty } from '@/store/login/actions';

import { selectLogin } from '@/store/login/selectors';

import { Button, Form, Input } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';

import { AUTH_TYPES } from '@shared/pages/Login/constants';
import LoginWrapper from '@shared/pages/Login/LoginWrapper';
import { selectCompany } from '@shared/store/company/selectors';
import debounce from '@shared/utils/debounce';
import { getPasswordRegex } from '@shared/utils/validate-utils';

import classNames from 'classnames';

import css from './Login.module.scss';

const AuthorizationPage = () => {
  const { loginUserRecaptcha } = useOutletContext();
  const { t } = useTranslation('authorization');
  const dispatch = useDispatch();

  const loginSelect = useSelector(selectLogin);
  const { company, providersTypes } = useSelector(selectCompany);

  const [isMasked, setIsMasked] = useState(true);
  const [form] = Form.useForm();

  const provider = company?.providers.find(provider => provider.type === AUTH_TYPES.INTERNAL && !provider.external);
  const ldapLogin = providersTypes?.includes(AUTH_TYPES.LDAP);

  const handleSubmit = formData => {
    loginUserRecaptcha(formData.password, formData.username);
  };

  useEffect(() => {
    dispatch(updateLoginEmpty());
  }, []);

  return (
    <LoginWrapper type='auth'>
      <Form className={css['LoginPage-form']} onFinish={handleSubmit} form={form}>
        <div className={css['LoginPage-form__content']}>
          <Form.Item
            colon={false}
            className={css['LoginPage-form-item']}
            name='username'
            validateStatus={loginSelect.error || form?.getFieldValue('username')?.length === 0 ? 'error' : 'success'}
            rules={[
              {
                required: true,
                message: t('mailRequired'),
              },
            ]}
          >
            <Input placeholder={provider?.usernameAlias} data-qa='loginUsernameInput' />
          </Form.Item>
          <Form.Item
            colon={false}
            className={css['LoginPage-form-item']}
            name='password'
            validateStatus={loginSelect.error || form?.getFieldValue('password')?.length === 0 ? 'error' : 'success'}
            rules={[
              {
                required: true,
                message: t('passwordRequired'),
              },
              {
                pattern: getPasswordRegex(ldapLogin),
                message: ldapLogin ? t('passwordWrongLdap') : t('passwordWrong'),
              },
            ]}
          >
            {ldapLogin ? (
              <div className={css['LoginPage-form-item-passwordLdap-block']}>
                <Input
                  className={classNames(css['LoginPage-form-item-passwordLdap'], {
                    [css['LoginPage-form-item-ldapPassword-hide']]: isMasked,
                  })}
                  type='text'
                  placeholder={t('password')}
                  data-qa='loginPasswordInput'
                  onChange={debounce(() => {
                    if (loginSelect.error) {
                      dispatch(updateLoginEmpty());
                    }
                  }, 300)}
                  addonAfter={
                    <Button
                      className={css['LoginPage-form-item-ldapPassword__toggle-button']}
                      onClick={() => setIsMasked(!isMasked)}
                      icon={isMasked ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    />
                  }
                />
              </div>
            ) : (
              <Input.Password
                className={css['LoginPage-form-item-password']}
                placeholder={t('password')}
                data-qa='loginPasswordInput'
                onChange={debounce(() => {
                  if (loginSelect.error) {
                    dispatch(updateLoginEmpty());
                  }
                }, 300)}
              />
            )}
          </Form.Item>
          {loginSelect.error && (
            <span className={css['LoginPage-form-item-error']} data-qa='loginErrorText'>
              {loginSelect.error}
            </span>
          )}
          <Form.Item className={css['LoginPage-form-google']}>
            <Button size='large' type={'primary'} htmlType='submit' name='ldap' data-qa='loginBtn'>
              {t('enter')}
            </Button>
          </Form.Item>
        </div>
      </Form>
    </LoginWrapper>
  );
};

export default AuthorizationPage;
